import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Tooltip from "../../../components/ui/Tooltip";
import { isMenuItemEnabled } from "../../../config/appsConfig";
import { mapChrome, mapChromeBase } from "../../../components/ui/mapChrome";

// Controles del mapa como píldoras flotando sobre el mapa (arriba a la izquierda).
// Antes eran dos íconos del encabezado (ojo = vistas, herramienta = utilidades)
// que había que abrir para saber qué estaba prendido. Daniel, 30-oct, con la
// referencia de ElevenLabs: lo que se prende y apaga a diario (satélite, geocercas,
// rutas) queda a la vista con su estado; lo demás va en «⋮».
//
// Monocromo a propósito: el color se reserva para lo que significa algo (estado
// de las unidades, alertas). Prendido = punto lleno; apagado = aro.

type MapType = "roadmap" | "satellite" | "hybrid" | "terrain";

export interface MapControlsProps {
  mapType?: MapType | null;
  setMapType?: (type: MapType) => void;
  showTraffic?: boolean;
  setShowTraffic?: (show: boolean) => void;
  showTransit?: boolean;
  setShowTransit?: (show: boolean) => void;
  showBicycling?: boolean;
  setShowBicycling?: (show: boolean) => void;
  showGeofences?: boolean;
  setShowGeofences?: (show: boolean) => void;
  showInterestPoints?: boolean;
  setShowInterestPoints?: (show: boolean) => void;
  showRoutes?: boolean;
  setShowRoutes?: (show: boolean) => void;
  showRuler?: boolean;
  setShowRuler?: (show: boolean) => void;
  showIcons?: boolean;
  setShowIcons?: (show: boolean) => void;
  showVehicleLabels?: boolean;
  setShowVehicleLabels?: (show: boolean) => void;
  showSearchBox?: boolean;
  showGrid?: boolean;
  onToggleGrid?: () => void;
  handleRulerToggle?: () => void;
  handleSearchBoxToggle?: () => void;
  onOpenPOIModal?: () => void;
  onOpenGeofenceModal?: () => void;
  onOpenTrackingLinkModal?: () => void;
}

const TYPES: MapType[] = ["roadmap", "satellite", "hybrid", "terrain"];
const TYPE_ICON: Record<MapType, string> = { roadmap: "ri-map-2-line", satellite: "ri-earth-line", hybrid: "ri-road-map-line", terrain: "ri-landscape-line" };
const typeKey = (m: MapType) => `mapViews.${m === "roadmap" ? "normal" : m}`;

export default function MapControlsBar({
  controls: c,
  isDarkMode,
  isReseller,
  selectedCompany,
}: {
  controls: MapControlsProps;
  isDarkMode: boolean;
  isReseller: boolean;
  selectedCompany?: string | null;
}) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState<"type" | "more" | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!barRef.current?.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const mapType: MapType = c.mapType || "roadmap";
  // Reseller sin compañía: crear geocercas, puntos o links no tiene a quién asignarlos.
  const needsCompany = isReseller && !selectedCompany;
  const canGeofences = isMenuItemEnabled("rastreo-telemetria", "admin", "geofences");
  const canPOI = isMenuItemEnabled("rastreo-telemetria", "admin", "pointOfInterests");
  const canLinks = isMenuItemEnabled("rastreo-telemetria", "admin", "tracking-links");

  const chrome = mapChrome(isDarkMode);
  const ui = {
    ...chrome,
    pill: chrome.surface,
    menu: isDarkMode ? "bg-[#1f1f1f]/95 border-white/10 text-gray-200" : "bg-white/95 border-black/[0.08] text-gray-700",
    dotOn: isDarkMode ? "bg-gray-100 border-gray-100" : "bg-gray-900 border-gray-900",
    dotOff: isDarkMode ? "border-gray-500" : "border-gray-400",
    knobOn: isDarkMode ? "bg-gray-900" : "bg-white",
  };

  const pill = `inline-flex items-stretch h-8 rounded-[10px] ${mapChromeBase} text-[13px] font-medium ${ui.pill}`;
  const seg = `flex items-center gap-1.5 px-2.5 transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] first:rounded-l-[9px] last:rounded-r-[9px] ${ui.hover}`;
  const dot = (on: boolean) => (
    <span aria-hidden className={`size-2 rounded-full border transition-colors duration-150 ${on ? ui.dotOn : `bg-transparent ${ui.dotOff}`}`} />
  );

  return (
    <div ref={barRef} className="absolute top-3 left-3 z-20 flex flex-wrap items-start gap-2 pointer-events-none [&>*]:pointer-events-auto">
      {/* Tipo de mapa: el punto alterna normal ↔ satélite; la flecha abre los cuatro tipos y las capas. */}
      <div className="relative">
        <div className={pill}>
          <button
            type="button"
            aria-pressed={mapType !== "roadmap"}
            onClick={() => c.setMapType?.(mapType === "roadmap" ? "satellite" : "roadmap")}
            className={seg}
          >
            {dot(mapType !== "roadmap")}
            <i className={`${TYPE_ICON[mapType === "roadmap" ? "satellite" : mapType]} text-[15px] ${ui.muted}`} aria-hidden />
            <span className="hidden sm:inline">{t(typeKey(mapType === "roadmap" ? "satellite" : mapType))}</span>
          </button>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={open === "type"}
            aria-label={t("mapViews.title")}
            onClick={() => setOpen(open === "type" ? null : "type")}
            className={`${seg} border-l ${ui.line} px-2`}
          >
            <i className={`ri-arrow-down-s-line text-base ${ui.muted} transition-transform duration-200 ${open === "type" ? "rotate-180" : ""}`} aria-hidden />
          </button>
        </div>
        {open === "type" && (
          <Menu className={ui.menu}>
            {TYPES.map((m) => (
              <Row key={m} icon={TYPE_ICON[m]} hover={ui.hover} muted={ui.muted} onClick={() => { c.setMapType?.(m); setOpen(null); }} aria-pressed={mapType === m}>
                {t(typeKey(m))}
                {mapType === m && <i className="ri-check-line ml-auto text-base" aria-hidden />}
              </Row>
            ))}
            <Divider line={ui.line} />
            <Toggle icon="ri-price-tag-3-line" label={t("mapViews.vehicleLabels")} on={c.showVehicleLabels ?? true} onToggle={() => c.setShowVehicleLabels?.(!(c.showVehicleLabels ?? true))} ui={ui} />
            <Toggle icon="ri-traffic-light-line" label={t("mapViews.showTraffic")} on={!!c.showTraffic} onToggle={() => c.setShowTraffic?.(!c.showTraffic)} ui={ui} />
            {import.meta.env.VITE_ENVIRONMENT === "development" && (
              <Toggle icon="ri-grid-line" label={t("mapTools.showGrid")} on={!!c.showGrid} onToggle={() => c.onToggleGrid?.()} ui={ui} />
            )}
          </Menu>
        )}
      </div>

      {/* Geocercas: el punto las muestra u oculta; el «+» crea una. */}
      <div className={pill}>
        <button type="button" aria-pressed={!!c.showGeofences} onClick={() => c.setShowGeofences?.(!c.showGeofences)} className={seg}>
          {dot(!!c.showGeofences)}
          <i className={`ri-shape-line text-[15px] ${ui.muted}`} aria-hidden />
          <span className="hidden sm:inline">{t("mapViews.geofences")}</span>
        </button>
        {canGeofences && (
          <Tooltip content={t("mapTools.selectCompanyFirst")} position="bottom" disabled={!needsCompany}>
            <button
              type="button"
              aria-label={t("mapTools.createGeofence")}
              disabled={needsCompany}
              onClick={() => c.onOpenGeofenceModal?.()}
              className={`${seg} h-full border-l ${ui.line} px-2 disabled:opacity-40 disabled:active:scale-100`}
            >
              <i className="ri-add-line text-base" aria-hidden />
            </button>
          </Tooltip>
        )}
      </div>

      <div className={pill}>
        <button type="button" aria-pressed={!!c.showRoutes} onClick={() => c.setShowRoutes?.(!c.showRoutes)} className={seg}>
          {dot(!!c.showRoutes)}
          <i className={`ri-route-line text-[15px] ${ui.muted}`} aria-hidden />
          <span className="hidden sm:inline">{t("mapViews.routes")}</span>
        </button>
      </div>

      {/* Lo de uso ocasional. */}
      <div className="relative">
        <div className={pill}>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={open === "more"}
            aria-label={t("mapTools.title")}
            onClick={() => setOpen(open === "more" ? null : "more")}
            className={`${seg} px-2`}
          >
            <i className="ri-more-2-fill text-base" aria-hidden />
          </button>
        </div>
        {open === "more" && (
          <Menu className={ui.menu}>
            <Toggle icon="ri-map-pin-line" label={t("mapViews.interestPoints")} on={!!c.showInterestPoints} onToggle={() => c.setShowInterestPoints?.(!c.showInterestPoints)} ui={ui} />
            <Divider line={ui.line} />
            <Row icon="ri-search-line" hover={ui.hover} muted={ui.muted} aria-pressed={!!c.showSearchBox} onClick={() => { c.handleSearchBoxToggle?.(); setOpen(null); }}>
              {t("mapTools.searchAddresses")}
              {c.showSearchBox && <i className="ri-check-line ml-auto text-base" aria-hidden />}
            </Row>
            <Row icon="ri-ruler-line" hover={ui.hover} muted={ui.muted} aria-pressed={!!c.showRuler} onClick={() => { c.handleRulerToggle?.(); setOpen(null); }}>
              {t("mapTools.ruler")}
              {c.showRuler && <i className="ri-check-line ml-auto text-base" aria-hidden />}
            </Row>
            <Row icon="ri-building-line" hover={ui.hover} muted={ui.muted} onClick={() => window.open("https://app.sct.gob.mx/sibuac_internet/ControllerUI?action=cmdEscogeRuta", "_blank", "noopener,noreferrer")}>
              {t("mapTools.tollBooths")}
              <i className={`ri-external-link-line ml-auto text-sm ${ui.muted}`} aria-hidden />
            </Row>
            {(canPOI || canLinks) && <Divider line={ui.line} />}
            {canPOI && (
              <Row icon="ri-map-pin-add-line" hover={ui.hover} muted={ui.muted} disabled={needsCompany} title={needsCompany ? t("mapTools.selectCompanyFirst") : undefined} onClick={() => { c.onOpenPOIModal?.(); setOpen(null); }}>
                {t("mapTools.createPOI")}
              </Row>
            )}
            {canLinks && (
              <Row icon="ri-links-line" hover={ui.hover} muted={ui.muted} disabled={needsCompany} title={needsCompany ? t("mapTools.selectCompanyFirst") : undefined} onClick={() => { c.onOpenTrackingLinkModal?.(); setOpen(null); }}>
                {t("mapTools.createTrackingLink")}
              </Row>
            )}
            <Divider line={ui.line} />
            <Row
              icon="ri-eraser-line"
              hover={ui.hover}
              muted={ui.muted}
              onClick={() => {
                c.setShowRuler?.(false);
                c.setShowTraffic?.(false);
                c.setShowTransit?.(false);
                c.setShowBicycling?.(false);
                c.setShowGeofences?.(false);
                c.setShowRoutes?.(false);
                c.setShowInterestPoints?.(false);
                c.setShowIcons?.(true);
                c.setMapType?.("roadmap");
                if (c.showGrid) c.onToggleGrid?.();
                setOpen(null);
              }}
            >
              {t("mapTools.clearMap")}
            </Row>
          </Menu>
        )}
      </div>
    </div>
  );
}

// Tarjeta del menú: cae desde su píldora (origen arriba a la izquierda), 150 ms.
function Menu({ className, children }: { className: string; children: ReactNode }) {
  return (
    <div
      role="menu"
      className={`absolute left-0 top-full mt-1.5 w-60 rounded-xl border p-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-xl motion-safe:animate-[mapMenuIn_150ms_cubic-bezier(0.23,1,0.32,1)] origin-top-left ${className}`}
    >
      {children}
      <style>{`@keyframes mapMenuIn { from { opacity: 0; transform: scale(0.97) translateY(-2px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}

function Divider({ line }: { line: string }) {
  return <div role="separator" className={`my-1 border-t ${line}`} />;
}

function Row({ icon, hover, muted, children, ...rest }: { icon: string; hover: string; muted: string; children: ReactNode } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      role="menuitem"
      {...rest}
      className={`w-full h-8 px-2.5 rounded-lg flex items-center gap-2.5 text-left text-[13px] transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${hover}`}
    >
      <i className={`${icon} text-[15px] ${muted}`} aria-hidden />
      {children}
    </button>
  );
}

// La fila entera es el interruptor. Prendido = pista oscura (claro) o clara (oscuro): sin verde.
function Toggle({ icon, label, on, onToggle, ui }: { icon: string; label: string; on: boolean; onToggle: () => void; ui: { hover: string; muted: string; dotOn: string; knobOn: string } }) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={on}
      onClick={onToggle}
      className={`w-full h-8 px-2.5 rounded-lg flex items-center gap-2.5 text-left text-[13px] transition-colors duration-150 ${ui.hover}`}
    >
      <i className={`${icon} text-[15px] ${ui.muted}`} aria-hidden />
      <span>{label}</span>
      <span aria-hidden className={`ml-auto relative inline-flex h-4 w-7 shrink-0 items-center rounded-full border transition-colors duration-200 ${on ? ui.dotOn : "border-transparent bg-gray-400/40"}`}>
        <span className={`inline-block size-3 rounded-full shadow-sm transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${on ? `translate-x-[13px] ${ui.knobOn}` : "translate-x-0.5 bg-white"}`} />
      </span>
    </button>
  );
}
